import { createContext, PropsWithChildren, useCallback, useContext, useEffect } from 'react';

import { useLocalStorage } from './useLocalStorage';

type OrganisationContextData = {
  id?: string;
  name?: string;
  organisationType?: string;
};

type OrganisationContextType = { orgData: OrganisationContextData; setOrgData: (v: OrganisationContextData) => void };

const ORGANISATION_KEY = 'CTX_SELECTED_ORGANISATION';

const Context = createContext<OrganisationContextType>({ orgData: {}, setOrgData: () => {} });

const useOrganisationContext = () => {
  return useContext(Context);
};

interface Props {
  /** Pass action's dispatch here if you want to sync local storage data with your Redux store */
  syncFn?: (v: OrganisationContextData) => void;
}

const OrganisationContext = ({ children, syncFn }: PropsWithChildren<Props>) => {
  const { storedValue: orgData, setValue } = useLocalStorage<OrganisationContextData>(ORGANISATION_KEY, {});

  useEffect(() => {
    syncFn && syncFn(orgData);
  }, [orgData, syncFn]);

  const setOrgData = useCallback(
    (v: OrganisationContextData) => {
      setValue(v);
      syncFn && syncFn(v);
    },
    [setValue, syncFn],
  );

  return <Context.Provider value={{ orgData, setOrgData }}>{children}</Context.Provider>;
};

export { ORGANISATION_KEY, OrganisationContext, useOrganisationContext };
export type { OrganisationContextData, OrganisationContextType };
